import {
  Alert,
  Box,
  ChevronDownIcon,
  ChevronUpIcon,
  Pagination,
  Switch,
  TextInput,
  ToggleGroup,
  Typography,
  useToaster
} from "@dealer-center/core-components";
import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import {
  useGenerateAltText,
  useGetImagesByAccountId,
  useSaveAltText
} from "@dealer-center/ddc-pages-alt-text-manager-fetch";
import type { MLAsset } from "@dealer-center/ddc-pages-alt-text-manager-interface";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ImageCard } from "./components/ImageCard";
import { SaveConfirmation } from "./components/SaveConfirmation";
import { useImagePagination } from "./hooks/useImagePagination";
import { processConcurrently } from "./utils/concurrentProcessor";

const ImageStatus = {
  Generating: "generating",
  Regenerating: "regenerating",
  Saving: "saving"
} as const;

type ImageStatusType = (typeof ImageStatus)[keyof typeof ImageStatus];

interface ImageState {
  altText?: string;
  savedAltText?: string;
  status?: ImageStatusType;
  hasLoadError: boolean;
  hasGenerationError: boolean;
  isAIGenerated: boolean;
  isLoaded: boolean;
}

const DEFAULT_IMAGE_STATE: ImageState = {
  hasLoadError: false,
  hasGenerationError: false,
  isAIGenerated: false,
  isLoaded: false
};

const PAGE_SIZE = 20;
const CONCURRENT_GENERATION_LIMIT = 5;
const GRID_MAX_WIDTH = 1050;

// Matches the Interstate toaster's auto-dismiss window (TOAST_TIMEOUT = 5000ms),
// measured from the most recent successful save.
const SAVE_CONFIRMATION_DISMISS_MS = 5000;
// Coalesces the screen-reader announcement to one utterance per burst: the
// polite live region is updated this long after the last successful save so a
// reader speaks a single message reflecting the final count, not one per save.
const SAVE_ANNOUNCE_DEBOUNCE_MS = 600;

// Standard visually-hidden ("sr-only") style: removed from the visual layout but
// still read by assistive technology. This is an offscreen SR region, not a
// repositioned toast.
const SR_ONLY_SX = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0
} as const;

const PROGRESS_COLOR_TRACK = "#e0e0e0";
const PROGRESS_COLOR_FILL = "#005ba8";
const PROGRESS_COLOR_COMPLETE = "#2e7d32";
const CARD_BORDER_SAVED = "#2e7d32";
const CARD_BORDER_AI_GENERATED = "#005ba8";

const getCardBorderAccent = (
  altText: string,
  savedAltText: string,
  apiAltText: string | null,
  isAIGenerated: boolean
): string | undefined => {
  const hasSavedAltText =
    (!!savedAltText && savedAltText === altText) ||
    (!!apiAltText?.trim() && altText === (apiAltText ?? ""));
  if (hasSavedAltText) return `4px solid ${CARD_BORDER_SAVED}`;
  const hasAIGeneratedUnsaved =
    isAIGenerated && !!altText?.trim() && !hasSavedAltText;
  if (hasAIGeneratedUnsaved) return `4px solid ${CARD_BORDER_AI_GENERATED}`;
  return undefined;
};

type FilterMode = "missing" | "all";

export const PagesAltTextManagerUiDisplay = () => {
  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntity;
  }>();
  const accountId = accountEntity?.id;
  const {
    data: imagesData,
    isLoading,
    isError
  } = useGetImagesByAccountId(accountId);
  const { mutateAsync: generateAltText } = useGenerateAltText();
  const { mutateAsync: saveAltText } = useSaveAltText();
  const [imageStates, setImageStates] = useState<Record<string, ImageState>>(
    {}
  );
  const toast = useToaster();
  const [filterMode, setFilterMode] = useState<FilterMode>("missing");
  const [enableAIGeneration, setEnableAIGeneration] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [generationProgress, setGenerationProgress] = useState<{
    total: number;
    completed: number;
    failed: number;
  } | null>(null);
  const [isProgressBarCollapsed, setIsProgressBarCollapsed] = useState(false);

  // Coalesced save confirmation: a single running count of successful saves
  // shown in the SaveConfirmation surface. Distinct from `savedCount` (the
  // progress memo), which tracks images with saved alt text across the dataset.
  const [saveConfirmationCount, setSaveConfirmationCount] = useState(0);
  // Polite, debounced screen-reader text for save confirmations.
  const [saveAnnouncement, setSaveAnnouncement] = useState("");
  // Assertive screen-reader text for save failures (announced on each failure).
  const [saveFailureAnnouncement, setSaveFailureAnnouncement] = useState("");
  const saveConfirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const saveAnnounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const getImageState = useCallback(
    (id: string): ImageState => imageStates[id] ?? DEFAULT_IMAGE_STATE,
    [imageStates]
  );

  const updateImageState = useCallback(
    (id: string, updates: Partial<ImageState>) => {
      setImageStates(prev => ({
        ...prev,
        [id]: {
          ...(prev[id] ?? DEFAULT_IMAGE_STATE),
          ...updates
        }
      }));
    },
    []
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const isGeneratingRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (saveConfirmationTimerRef.current) {
        clearTimeout(saveConfirmationTimerRef.current);
        saveConfirmationTimerRef.current = null;
      }
      if (saveAnnounceTimerRef.current) {
        clearTimeout(saveAnnounceTimerRef.current);
        saveAnnounceTimerRef.current = null;
      }
    };
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Stage 1: Search filter
  const searchFilteredImages = useMemo(() => {
    if (!imagesData) return [];
    const term = debouncedSearchTerm.trim().toLowerCase();
    if (!term) return imagesData;
    return imagesData.filter(
      image =>
        image.name.toLowerCase().includes(term) ||
        image.label.toLowerCase().includes(term)
    );
  }, [imagesData, debouncedSearchTerm]);

  // Single-pass consolidated memo: produces savedCount, missingCount, and effectiveSet.
  // "Has alt text" means SAVED only: imageStates[id]?.savedAltText?.trim() is non-empty
  // OR image.altText?.trim() is non-empty. Nothing else counts.
  const { savedCount, missingCount, effectiveSet } = useMemo(() => {
    let saved = 0;
    let missing = 0;
    const effective = new Set<string>();
    for (const image of searchFilteredImages) {
      const hasSavedLocal = !!imageStates[image.id]?.savedAltText?.trim();
      const hasApiAltText = !!image.altText?.trim();
      if (hasSavedLocal || hasApiAltText) {
        saved++;
        effective.add(image.id);
      } else {
        missing++;
      }
    }
    return {
      savedCount: saved,
      missingCount: missing,
      effectiveSet: effective
    };
  }, [searchFilteredImages, imageStates]);

  const filterCounts = {
    all: searchFilteredImages.length,
    missing: missingCount
  };

  const progressTotal = searchFilteredImages.length;
  const progressPercent =
    progressTotal > 0 ? (savedCount / progressTotal) * 100 : 0;
  const isComplete = savedCount === progressTotal && progressTotal > 0;
  const isProgressBarVisible = !isLoading && progressTotal > 0;
  const totalImageCount = imagesData?.length ?? 0;
  const isSearchFiltered = debouncedSearchTerm.trim() !== "";
  const isFilteredByToggle = filterMode !== "all";
  const isFiltered = isSearchFiltered || isFilteredByToggle;

  // Stage 2: Filter by alt text status using the unified effectiveSet.
  // An image is "missing" if it is NOT in effectiveSet (no saved alt text).
  const filteredImages = useMemo(
    () =>
      searchFilteredImages.filter(image => {
        if (filterMode === "all") return true;
        return !effectiveSet.has(image.id);
      }),
    [searchFilteredImages, filterMode, effectiveSet]
  );

  const { currentPage, setCurrentPage, totalPages, imagesOnPage } =
    useImagePagination({
      filteredImages,
      pageSize: PAGE_SIZE,
      resetTrigger: `${filterMode}-${debouncedSearchTerm}`
    });

  useEffect(() => {
    if (
      !enableAIGeneration ||
      !imagesOnPage ||
      imagesOnPage.length === 0 ||
      isGeneratingRef.current === currentPage
    )
      return;

    const imagesToGenerate = imagesOnPage.filter(image => {
      const state = getImageState(image.id);
      return (
        state.isLoaded &&
        !state.status &&
        !state.hasGenerationError &&
        !state.altText &&
        !state.savedAltText &&
        (!image.altText || image.altText.trim() === "")
      );
    });

    if (imagesToGenerate.length === 0) return;

    // Abort any previous batch before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Set ref to prevent duplicate batch executions
    isGeneratingRef.current = currentPage;

    setGenerationProgress({
      total: imagesToGenerate.length,
      completed: 0,
      failed: 0
    });

    const generateForAllImages = async () => {
      try {
        await processConcurrently(
          imagesToGenerate,
          CONCURRENT_GENERATION_LIMIT,
          async image => {
            if (controller.signal.aborted) return;
            const success = await autoGenerateAltText(image, controller.signal);
            if (controller.signal.aborted) return;
            setGenerationProgress(prev =>
              prev
                ? {
                    ...prev,
                    completed: prev.completed + 1,
                    failed: prev.failed + (success ? 0 : 1)
                  }
                : prev
            );
          },
          controller.signal
        );
      } finally {
        if (abortControllerRef.current === controller) {
          isGeneratingRef.current = null;
        }
      }
    };

    generateForAllImages();
  }, [enableAIGeneration, imagesOnPage, imageStates]);

  useEffect(() => {
    if (!enableAIGeneration) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isGeneratingRef.current = null;
      setGenerationProgress(null);
    }
  }, [enableAIGeneration]);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isGeneratingRef.current = null;
    setGenerationProgress(null);
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, [currentPage]);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isGeneratingRef.current = null;
    setGenerationProgress(null);
  }, [filterMode, debouncedSearchTerm]);

  const autoGenerateAltText = async (
    image: MLAsset,
    signal?: AbortSignal
  ): Promise<boolean> => {
    const state = getImageState(image.id);

    // Skip if already generating or has alt text
    if (
      state.status === ImageStatus.Generating ||
      state.status === ImageStatus.Regenerating
    )
      return true;
    if (state.altText) return true;

    updateImageState(image.id, { status: ImageStatus.Generating });

    try {
      const response = await generateAltText({
        imageName: image.name,
        signal
      });
      updateImageState(image.id, {
        altText: response.altText,
        hasGenerationError: false,
        isAIGenerated: true,
        status: undefined
      });
      return true;
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        updateImageState(image.id, { status: undefined });
        return false;
      }
      console.error(
        "Error:",
        image.label,
        error instanceof Error ? error.message : error
      );
      updateImageState(image.id, {
        altText: undefined,
        hasGenerationError: true,
        status: undefined
      });
      return false;
    }
  };

  const handleRegenerateAltText = async (image: MLAsset) => {
    updateImageState(image.id, { status: ImageStatus.Regenerating });
    try {
      const response = await generateAltText({ imageName: image.name });
      updateImageState(image.id, {
        altText: response.altText,
        hasGenerationError: false,
        isAIGenerated: true,
        status: undefined
      });
      toast.toastSuccess("Regenerated", "New text generated");
    } catch (error) {
      console.error(
        "Error:",
        image.label,
        error instanceof Error ? error.message : error
      );
      toast.toastError("Failed to generate alt text");
      updateImageState(image.id, {
        hasGenerationError: true,
        status: undefined
      });
    }
  };

  const handleAltTextChange = (imageKey: string, value: string) => {
    if (value === getImageState(imageKey).altText) return;
    updateImageState(imageKey, { altText: value, isAIGenerated: false });
  };

  const isImageGenerating = (imageId: string) =>
    getImageState(imageId).status === ImageStatus.Generating;

  const hasAltText = (image: MLAsset) =>
    !!(getImageState(image.id).altText || image.altText);

  // Coalesce a successful save into the single running confirmation: increment
  // the visible count (a pure functional update, so out-of-order resolutions
  // each add exactly one) and (re)start the dismiss timer measured from the
  // most recent success. The polite announcement is debounced separately in the
  // effect below, keyed off the count.
  const registerSaveSuccess = useCallback(() => {
    setSaveConfirmationCount(prev => prev + 1);

    if (saveConfirmationTimerRef.current) {
      clearTimeout(saveConfirmationTimerRef.current);
    }
    saveConfirmationTimerRef.current = setTimeout(() => {
      setSaveConfirmationCount(0);
      setSaveAnnouncement("");
    }, SAVE_CONFIRMATION_DISMISS_MS);
  }, []);

  // Debounce the polite announcement off the latest count so the reader hears a
  // single utterance reflecting the final count of the burst, not one per save.
  useEffect(() => {
    if (saveConfirmationCount <= 0) return;
    if (saveAnnounceTimerRef.current) {
      clearTimeout(saveAnnounceTimerRef.current);
    }
    saveAnnounceTimerRef.current = setTimeout(() => {
      setSaveAnnouncement(`${saveConfirmationCount} saved`);
    }, SAVE_ANNOUNCE_DEBOUNCE_MS);
    return () => {
      if (saveAnnounceTimerRef.current) {
        clearTimeout(saveAnnounceTimerRef.current);
        saveAnnounceTimerRef.current = null;
      }
    };
  }, [saveConfirmationCount]);

  const announceSaveFailure = useCallback((message: string) => {
    setSaveFailureAnnouncement(message);
  }, []);

  const handleSaveAltText = async (image: MLAsset) => {
    const state = getImageState(image.id);
    const altTextValue = state.altText || "";
    updateImageState(image.id, { status: ImageStatus.Saving });

    try {
      await saveAltText({
        assetId: image.id,
        altText: altTextValue
      });
      updateImageState(image.id, {
        savedAltText: altTextValue,
        status: undefined
      });
      registerSaveSuccess();
    } catch (error) {
      // Keep the underlying detail (e.g. an HTTP status text) in the console for
      // diagnostics, but surface only the fixed copy to the user and the screen
      // reader — never reflect raw server error text into the UI.
      console.error(`Failed to save alt text for image ${image.id}:`, error);
      const failureMessage = "Failed to save alt text.";
      toast.toastError(failureMessage);
      announceSaveFailure(failureMessage);
      updateImageState(image.id, { status: undefined });
    }
  };

  return (
    <Box data-testid="alt-text-image-grid" ref={containerRef}>
      <SaveConfirmation count={saveConfirmationCount} />
      <Box
        aria-atomic="true"
        aria-live="polite"
        data-testid="save-confirmation-live-region"
        role="status"
        sx={SR_ONLY_SX}
      >
        {saveAnnouncement}
      </Box>
      <Box
        aria-live="assertive"
        data-testid="save-failure-live-region"
        role="alert"
        sx={SR_ONLY_SX}
      >
        {saveFailureAnnouncement}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mb: 3,
          maxWidth: GRID_MAX_WIDTH
        }}
      >
        <Typography data-testid="page-description" variant="body-md">
          AI-generated alt text for Media Library images
        </Typography>
        <Alert data-testid="ai-disclaimer" sx={{ my: 2 }} type="caution">
          Disclaimer: This tool uses AI technology. AI-generated outputs may
          contain errors, omissions, or inaccuracies. Users are responsible for
          independently reviewing, verifying, and validating all AI-generated
          content before relying on it for any purpose.
        </Alert>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <TextInput
            data-testid="image-search-input"
            name="image-search"
            placeholder="Search images by name"
            sx={{ width: "25ch" }}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Box
            sx={{
              "& .MuiToggleButtonGroup-root": {
                borderRadius: "8px",
                boxShadow:
                  "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
                overflow: "hidden"
              },
              "& .MuiToggleButton-root": {
                padding: "8px 32px !important",
                minWidth: "180px",
                borderRadius: "8px !important",
                backgroundColor: "#ffffff !important",
                color: "#333333 !important",
                "&:hover": {
                  backgroundColor: "#f5f5f5 !important"
                }
              },
              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: `${PROGRESS_COLOR_FILL} !important`,
                color: "#ffffff !important",
                "& .MuiTypography-root": {
                  color: "#ffffff !important"
                },
                "&:hover": {
                  backgroundColor: "#0069c2 !important"
                }
              },
              "& .MuiToggleButton-root > .MuiBox-root": {
                padding: "0 !important"
              }
            }}
          >
            <ToggleGroup
              data-testid="alt-text-filter"
              defaultSelected="missing"
              toggleButtonOptions={[
                {
                  value: "missing",
                  children: (
                    <Typography sx={{ color: "inherit" }} variant="body-sm">
                      Missing Alt Text ({filterCounts.missing})
                    </Typography>
                  )
                },
                {
                  value: "all",
                  children: (
                    <Typography sx={{ color: "inherit" }} variant="body-sm">
                      All Images ({filterCounts.all})
                    </Typography>
                  )
                }
              ]}
              toggleWidth="auto"
              onChange={(_event: React.MouseEvent, value: string | null) => {
                if (value) setFilterMode(value as FilterMode);
              }}
            />
          </Box>
          <Box sx={{ marginLeft: "auto" }}>
            <Switch
              checked={enableAIGeneration}
              data-testid="enable-ai-generation-toggle"
              label="Enable AI Generation"
              value={enableAIGeneration}
              onClick={e => setEnableAIGeneration(e.target.value === "checked")}
            />
          </Box>
        </Box>
      </Box>

      {enableAIGeneration && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mb: 2,
            maxWidth: GRID_MAX_WIDTH
          }}
        >
          <Alert data-testid="ai-generation-info" type="info">
            AI will automatically generate alt text for images without it as you
            browse pages.
          </Alert>
          {generationProgress && filteredImages.length > 0 && (
            <Typography data-testid="generation-progress" variant="body-sm">
              {generationProgress.completed < generationProgress.total
                ? `Generating ${generationProgress.completed + 1} of ${generationProgress.total} on this page...`
                : generationProgress.failed > 0
                  ? `Done - ${generationProgress.total - generationProgress.failed} of ${generationProgress.total} images generated on this page`
                  : `Done - ${generationProgress.total} images generated on this page`}
            </Typography>
          )}
        </Box>
      )}

      {isProgressBarVisible && (
        <Box
          data-testid="alt-text-progress-bar"
          sx={{
            mb: 2,
            maxWidth: GRID_MAX_WIDTH
          }}
        >
          {isProgressBarCollapsed ? (
            <Box
              aria-label="Expand progress bar"
              data-testid="alt-text-progress-collapsed"
              role="button"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer"
              }}
              tabIndex={0}
              onClick={() => setIsProgressBarCollapsed(false)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setIsProgressBarCollapsed(false);
                }
              }}
            >
              <ChevronDownIcon
                data-testid="progress-expand-icon"
                desc="Expand progress bar"
                title="Expand"
              />
              <Typography
                data-testid="alt-text-progress-label"
                key={savedCount}
                variant="body-sm"
              >
                {savedCount}/{progressTotal} saved
                {isFiltered ? ` (${totalImageCount} total)` : ""}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5
              }}
            >
              <Box
                aria-label="Collapse progress bar"
                data-testid="progress-collapse-toggle"
                role="button"
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center"
                }}
                tabIndex={0}
                onClick={() => setIsProgressBarCollapsed(true)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsProgressBarCollapsed(true);
                  }
                }}
              >
                <ChevronUpIcon
                  data-testid="progress-collapse-icon"
                  desc="Collapse progress bar"
                  title="Collapse"
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: "4px",
                  backgroundColor: PROGRESS_COLOR_TRACK
                }}
              >
                <Box
                  data-complete={isComplete}
                  data-testid="alt-text-progress-fill"
                  sx={{
                    height: "100%",
                    width: `${progressPercent}%`,
                    borderRadius: "4px",
                    backgroundColor: isComplete
                      ? PROGRESS_COLOR_COMPLETE
                      : PROGRESS_COLOR_FILL,
                    transition: "width 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                    "@keyframes shimmer": {
                      "0%": {
                        transform: "translateX(-100%)"
                      },
                      "100%": {
                        transform: "translateX(100%)"
                      }
                    },
                    '&[data-complete="true"]::after': {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)",
                      animation: "shimmer 1s 1 forwards"
                    }
                  }}
                />
              </Box>
              <Typography
                data-testid="alt-text-progress-label"
                key={savedCount}
                sx={{
                  "@keyframes fadeIn": {
                    "0%": { opacity: 0 },
                    "100%": { opacity: 1 }
                  },
                  animation: "fadeIn 0.3s forwards",
                  whiteSpace: "nowrap"
                }}
                variant="body-sm"
              >
                Images with saved alt text: {savedCount} of {progressTotal}
                {isSearchFiltered ? " matching" : ""}{" "}
                {progressTotal === 1 ? "image" : "images"}
                {isFiltered ? ` (${totalImageCount} total)` : ""}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {isLoading && (
        <Box
          data-testid="alt-text-manager-loading"
          sx={{ display: "flex", justifyContent: "center", p: 4 }}
        >
          <Typography variant="body-md">Loading images...</Typography>
        </Box>
      )}

      {!isLoading && filteredImages.length === 0 && (
        <Typography data-testid="empty-state" variant="body-md">
          {debouncedSearchTerm.trim()
            ? "No images match your filter"
            : filterMode === "missing" && searchFilteredImages.length > 0
              ? "All images have alt text. Switch to 'All Images' to review them."
              : "No images found"}
        </Typography>
      )}

      {!isLoading && filteredImages && filteredImages.length > 0 && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 2,
              maxWidth: GRID_MAX_WIDTH
            }}
          >
            {imagesOnPage.map((image, index) => {
              const state = getImageState(image.id);
              const altTextValue = state.altText ?? image.altText ?? "";
              const savedAltTextValue = state.savedAltText ?? "";
              const borderLeftAccent = getCardBorderAccent(
                altTextValue,
                savedAltTextValue,
                image.altText,
                state.isAIGenerated
              );
              const nextImageId =
                index < imagesOnPage.length - 1
                  ? imagesOnPage[index + 1].id
                  : undefined;
              return (
                <ImageCard
                  altText={altTextValue}
                  borderLeftAccent={borderLeftAccent}
                  hasAltText={hasAltText(image)}
                  hasError={state.hasLoadError}
                  hasGenerationError={state.hasGenerationError}
                  image={image}
                  isGenerating={isImageGenerating(image.id)}
                  isRegenerating={state.status === ImageStatus.Regenerating}
                  isSaving={state.status === ImageStatus.Saving}
                  key={image.id}
                  nextImageId={nextImageId}
                  savedAltText={savedAltTextValue}
                  onAltTextChange={handleAltTextChange}
                  onImageError={imageId =>
                    updateImageState(imageId, { hasLoadError: true })
                  }
                  onImageLoad={imageId => {
                    const existing = getImageState(imageId);
                    updateImageState(imageId, {
                      isLoaded: true,
                      ...(existing.savedAltText === undefined && {
                        savedAltText: image.altText ?? ""
                      })
                    });
                  }}
                  onRegenerate={handleRegenerateAltText}
                  onSave={handleSaveAltText}
                />
              );
            })}
          </Box>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={setCurrentPage}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
